/**
 * The audit log viewer — reads `audit_log`, written by the DB trigger `enable_audit()` attaches to
 * an opt-in list of tables (see `packages/db/prisma/sql/audit.sql`'s own list and its reasoning for
 * each table), plus a handful of explicit application writes for actions that touch no single
 * audited row (e.g. attendance cycle freeze/unfreeze — a bulk UPDATE across many `attendance_days`
 * rows, which is not itself an audited table).
 *
 * READ-ONLY by design. Nothing here ever writes to `audit_log` — every write goes through the
 * trigger or an explicit `tx.auditLog.create()` at the call site that made the change, so the log
 * only ever grows from something that actually happened, never from viewing it.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, paginatedSchema, paginationQuerySchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const auditActorTypeSchema = z.enum(['user', 'system', 'ai']);

export const auditTrailEntrySchema = z.object({
  id: uuidSchema,
  actorPersonId: uuidSchema.nullable(),
  actorName: z.string().nullable(),
  impersonatorPersonId: uuidSchema.nullable(),
  impersonatorName: z.string().nullable(),
  actorType: auditActorTypeSchema,
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  /** Field-level diff, as the trigger wrote it — PII values already redacted at write time. */
  before: z.record(z.unknown()).nullable(),
  after: z.record(z.unknown()).nullable(),
  ipAddress: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditTrailEntry = z.infer<typeof auditTrailEntrySchema>;

export const listAuditLogQuerySchema = paginationQuerySchema.extend({
  entityType: z.string().optional(),
  actorPersonId: uuidSchema.optional(),
  action: z.string().optional(),
  /** Inclusive. Both ISO date-time strings, matching `createdAt`'s own wire shape. */
  from: z.string().optional(),
  to: z.string().optional(),
});
export type ListAuditLogQuery = z.infer<typeof listAuditLogQuerySchema>;

export const auditContract = c.router(
  {
    listAuditLog: {
      method: 'GET',
      path: '/audit-log',
      query: listAuditLogQuerySchema,
      responses: {
        200: paginatedSchema(auditTrailEntrySchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta('audit:read'),
      summary: 'The audit trail: who changed what, and when — filterable, paginated, newest first',
    },

    listAuditLogEntityTypes: {
      method: 'GET',
      path: '/audit-log/entity-types',
      responses: {
        200: z.object({ entityTypes: z.array(z.string()) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta('audit:read'),
      summary: 'Distinct entity types present in this org’s audit log, for the filter dropdown',
    },
  },
  { pathPrefix: '/audit' },
);
